import { useCallback, useEffect, useState } from 'react';
import {
  confirmDraft,
  deleteDraftEntry,
  fetchDraft,
  swapDraftEntry,
} from '../api/planner.js';
import type { DraftResponse } from '../api/plannerTypes.js';

export interface UsePlannerDraftResult {
  data: DraftResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  swap: (id: number, payload: { recipe_id?: number; portions?: number }) => Promise<void>;
  remove: (id: number) => Promise<void>;
  confirm: () => Promise<{ confirmed_count: number }>;
}

export function usePlannerDraft(): UsePlannerDraftResult {
  const [data, setData] = useState<DraftResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchDraft();
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load draft');
    } finally {
      setLoading(false);
    }
  }, []);

  const swap = useCallback(
    async (id: number, payload: { recipe_id?: number; portions?: number }): Promise<void> => {
      await swapDraftEntry(id, payload);
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: number): Promise<void> => {
      await deleteDraftEntry(id);
      await refresh();
    },
    [refresh],
  );

  const confirm = useCallback(async (): Promise<{ confirmed_count: number }> => {
    const res = await confirmDraft();
    await refresh();
    return res;
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh, swap, remove, confirm };
}